<template>
  <div class="ep-wrap" ref="wrapRef">
    <button type="button" class="ep-toggle" @click.stop="togglePicker" title="إضافة إيموجي" aria-label="فتح قائمة الإيموجي">
      😊
    </button>

    <Transition v-if="!isMobilePicker" name="ep-pop">
      <div
        v-if="open"
        class="ep-popup"
        @click.stop
      >
        <emoji-picker @emoji-click="onEmojiClick" class="ep-picker"></emoji-picker>
      </div>
    </Transition>
    <Teleport v-else to="body">
      <Transition name="ep-pop">
        <div
          v-if="open"
          ref="popupRef"
          class="ep-popup is-mobile-positioned"
          :style="mobilePosition || undefined"
          @click.stop
        >
          <emoji-picker @emoji-click="onEmojiClick" class="ep-picker"></emoji-picker>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { nextTick, ref, onMounted, onBeforeUnmount } from 'vue';
import 'emoji-picker-element';

const emit = defineEmits(['emoji-select']);
const open = ref(false);
const wrapRef = ref(null);
const popupRef = ref(null);
const mobilePosition = ref(null);
const isMobilePicker = ref(false);

function isMobileViewport() {
  return window.innerWidth <= 768;
}

function togglePicker() {
  open.value = !open.value;
  isMobilePicker.value = isMobileViewport();
  if (open.value) {
    nextTick(updateMobilePosition);
  } else {
    mobilePosition.value = null;
  }
}

function updateMobilePosition() {
  isMobilePicker.value = isMobileViewport();
  if (!open.value || !wrapRef.value || !popupRef.value || !isMobilePicker.value) {
    mobilePosition.value = null;
    return;
  }
  const margin = 8;
  const viewport = window.visualViewport;
  const viewportWidth = Math.floor(Math.min(
    document.documentElement.clientWidth || window.innerWidth,
    window.innerWidth,
    viewport?.width || window.innerWidth
  ));
  const viewportHeight = Math.floor(Math.min(
    document.documentElement.clientHeight || window.innerHeight,
    window.innerHeight,
    viewport?.height || window.innerHeight
  ));
  const triggerRect = wrapRef.value.getBoundingClientRect();
  const popupWidth = Math.floor(Math.min(popupRef.value.offsetWidth || 340, viewportWidth - margin * 2));
  const popupHeight = Math.floor(Math.min(popupRef.value.offsetHeight || 420, viewportHeight - margin * 2));
  const spaceAbove = triggerRect.top - margin;
  const spaceBelow = viewportHeight - triggerRect.bottom - margin;
  const preferredTop = spaceAbove >= popupHeight || spaceAbove > spaceBelow
    ? Math.max(margin, triggerRect.top - popupHeight - 10)
    : Math.min(viewportHeight - popupHeight - margin, triggerRect.bottom + 10);
  const preferredRight = viewportWidth - triggerRect.right;
  const leftFromRight = viewportWidth - preferredRight - popupWidth;
  const top = Math.floor(Math.min(
    Math.max(margin, preferredTop),
    Math.max(margin, viewportHeight - popupHeight - margin)
  ));
  const left = Math.floor(Math.min(
    Math.max(margin, leftFromRight),
    Math.max(margin, viewportWidth - popupWidth - margin)
  ));
  mobilePosition.value = {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    right: "auto",
    bottom: "auto",
    width: `${popupWidth}px`,
    maxWidth: `calc(100vw - ${margin * 2}px)`,
    maxHeight: `${popupHeight}px`,
  };
}

function onEmojiClick(event) {
  emit('emoji-select', event.detail.unicode);
  open.value = false;
  mobilePosition.value = null;
}

function handleOutsideClick(e) {
  const clickedToggle = wrapRef.value?.contains(e.target);
  const clickedPopup = popupRef.value?.contains(e.target);
  if (open.value && !clickedToggle && !clickedPopup) {
    open.value = false;
    mobilePosition.value = null;
  }
}

onMounted(() => {
  document.addEventListener('click', handleOutsideClick);
  window.addEventListener('resize', updateMobilePosition);
  window.visualViewport?.addEventListener('resize', updateMobilePosition);
  window.visualViewport?.addEventListener('scroll', updateMobilePosition);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick);
  window.removeEventListener('resize', updateMobilePosition);
  window.visualViewport?.removeEventListener('resize', updateMobilePosition);
  window.visualViewport?.removeEventListener('scroll', updateMobilePosition);
});
</script>

<style scoped>
.ep-wrap { position: relative; display: inline-block; margin-bottom: 0.25rem; }

.ep-toggle {
  background: rgba(5, 22, 41,.08);
  border: 1.5px solid rgba(5, 22, 41,.15);
  border-radius: 10px;
  padding: .35rem .55rem;
  font-size: 1.2rem; cursor: pointer; line-height: 1;
  transition: all .2s;
}
.ep-toggle:hover {
  background: rgba(5, 22, 41,.16);
  border-color: rgba(5, 22, 41,.3);
  transform: scale(1.1);
}

.ep-popup {
  position: absolute;
  bottom: calc(100% + 10px);
  right: 0;
  z-index: 9999;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0,0,0,.22), 0 0 0 1px rgba(5, 22, 41,.12);
  background: rgba(255,255,255,.98);
  backdrop-filter: blur(20px);
}

.ep-popup.is-mobile-positioned {
  box-sizing: border-box;
  overflow: hidden;
  z-index: 1065;
}

/* Transition */
.ep-pop-enter-active, .ep-pop-leave-active { transition: all .2s cubic-bezier(.4,0,.2,1); }
.ep-pop-enter-from, .ep-pop-leave-to { opacity: 0; transform: translateY(8px) scale(.97); }

/* emoji-picker-element skin */
.ep-picker {
  --background: transparent;
  --border-color: rgba(5, 22, 41,.1);
  --button-hover-background: rgba(5, 22, 41,.08);
  --category-font-color: #051629;
  --emoji-size: 1.5rem;
  border: none;
  width: min(340px, 100%);
  max-height: inherit;
}
</style>
